import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useTeamListEvents,
} from 'app/frontend/base/config/queries'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useEventParticipantStatusMutation from './useEventParticipantStatusMutation'
import EventListTable from './EventListTable'
import useBulkUserEventStatusMutation from './useBulkUserEventStatusMutation'

const isEventPageValue = (value: string): value is string => /^\d+$/.test(value)

const EventList = () => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const navigate = useNavigate()
  const {
    data: { user },
  } = usePageInfo()
  const { value: pageValue, setValue: setPageValue } =
    usePersistedHashValue<string>({
      defaultValue: '1',
      isValid: isEventPageValue,
      hashKey: 'page',
      persistInLocalStorage: false,
    })
  const page = Math.max(Number(pageValue), 1)
  const { data, isPending, isFetching } = useTeamListEvents(team.id, page)
  const isLoadingEvents =
    isPending || (isFetching && (data?.page ?? page) !== page)
  const [updatingEventId, setUpdatingEventId] = useState<number>()
  const ownMembership = useMemo(
    () =>
      (team.members ?? []).find(
        (member) => member.characterReference.userId === user?.id,
      ),
    [team.members, user?.id],
  )
  const { selectableStatuses, updateStatus, isUpdatingStatus } =
    useEventParticipantStatusMutation(
      {
        characterId: ownMembership?.characterReference.id ?? 0,
        role: ownMembership?.role ?? 'ranged',
        specId: ownMembership?.specId ?? undefined,
      },
      {
        onSuccess: () => setUpdatingEventId(undefined),
        onError: () => setUpdatingEventId(undefined),
      },
    )
  const { bulkUpdateStatus, isBulkUpdating } = useBulkUserEventStatusMutation()

  return (
    <EventListTable
      userId={user?.id}
      events={data?.events ?? []}
      page={data?.page ?? page}
      totalPages={data?.totalPages ?? 1}
      isLoadingEvents={isLoadingEvents}
      isUpdatingStatus={isUpdatingStatus}
      updatingEventId={updatingEventId}
      onPageChange={(nextPage) => setPageValue(nextPage.toString())}
      onRowClick={(event) => navigate(`${team.path}/events/${event.id}`)}
      membershipForEvent={() => ownMembership}
      selectableStatusesForEvent={() => selectableStatuses}
      canAdminEditEvent={() => accessLevel === 'admin'}
      team={team}
      onSelectStatus={({ event, participant, nextStatus }) => {
        setUpdatingEventId(event.id)
        updateStatus({
          eventId: event.id,
          eventParticipantId: participant?.id,
          nextStatus,
        })
      }}
      bulkSelectableStatuses={selectableStatuses}
      isBulkUpdating={isBulkUpdating}
      onBulkSelectStatus={(eventIds, nextStatus) => {
        bulkUpdateStatus({ eventIds, status: nextStatus })
      }}
    />
  )
}

export default EventList
